<template>
  <Dialog
    v-model="dialog"
    header-type="information"
    left-header-icon="icons/shield.svg"
    :title="$t('prevention.personalization.banner.title')"
    :buttons="buttons"
  >
    <template #text>
      <div class="mb-6">
        {{ $t('prevention.subtitle') }}
      </div>
      <DateSegmented
        class="mb-6"
        required
        :date-help-text="$t('prevention.birthDateNote')"
        :initial-value="$auth.user.birthDate"
        identifier="prevention_date"
        @blur="onDateBlur"
        @focus="
          gtmPush({
            event: 'form_progress',
            'form.field': $event.target.dataset.part,
            'form.name': formName
          })
        "
      />
      <Sex required class="mb-6" :form-name="formName" event-sufix="prevention_date" />
    </template>
  </Dialog>
</template>

<script>
import Dialog from '@/components/Atoms/Dialog';
import DateSegmented from '@/components/Forms/Parts/DateSegmented.vue';
import Sex from '@/components/Forms/Parts/Sex.vue';
import preventionData from '@/mixins/preventionData';
import checkUserVerification from '@/mixins/checkUserVerification';
import { mapActions, mapGetters, mapMutations } from 'vuex';

export default {
  name: 'PreventionPersonalizationDialog',
  components: { Sex, DateSegmented, Dialog },
  mixins: [preventionData, checkUserVerification],
  data() {
    return {
      loading: false,
      formName: 'prevence'
    };
  },
  computed: {
    ...mapGetters({
      isValidDate: 'forms/date/isValid',
      date: 'forms/date/date',

      isValidSex: 'forms/sex/isValid',
      sex: 'forms/sex/sex'
    }),
    dialog: {
      get() {
        return this.$store.state.profile.dashboard.dialog === 'preventionPersonalization';
      },
      set(value) {
        this.$store.commit('profile/dashboard/setDialog', value);
      }
    },
    buttons() {
      return [
        {
          type: 'sendButton',
          props: {
            loading: this.loading
          },
          events: {
            click: async () => {
              await this.validateForm();
            }
          },
          text: this.$t('send')
        }
      ];
    }
  },
  watch: {
    dialog(newValue) {
      if (newValue) {
        this.updateSex('');
        this.updateDate('');
        if (this.$auth.user.birthDate) {
          let birthDate = new Date(this.$auth.user.birthDate);
          this.$store.commit(
            'forms/date/updateDate',
            birthDate.getFullYear() + '-' + (birthDate.getMonth() + 1) + '-' + birthDate.getDate()
          );
        }
        this.updateSex(this.$auth.user.gender || null);
        this.gtmPush({ event: 'form_start', 'form.name': 'prevence' });
        this.gtmPush({ event: 'form_started', 'form.name': 'prevence' });
      }
    }
  },
  methods: {
    ...mapMutations({
      updateSex: 'forms/sex/updateSex',
      updateDate: 'forms/date/updateDate'
    }),
    ...mapActions({
      getAllDefaultFormData: 'dynamicForms/getAllDefaultFormData'
    }),
    onDateBlur() {
      document.querySelector(`#inputGender_${this.formName}`).focus();
    },
    validateForm() {
      if (this.date && this.sex) {
        this.$eventHub.$emit('validateDateSegmented_prevention_date', () => {
          this.$eventHub.$emit('validateSexprevention_date', () => {
            this.submitForm();
          });
        });
      } else if (this.date) {
        this.$eventHub.$emit('validateDateSegmented_prevention_date', () => {
          this.submitForm();
        });
      } else if (this.sex) {
        this.$eventHub.$emit('validateSexprevention_date', () => {
          this.submitForm();
        });
      } else {
        this.dialog = false;
      }
    },
    submitForm() {
      if (
        (this.date && this.isValidDate && !this.sex) ||
        (this.sex && this.isValidSex && !this.date) ||
        (this.date && this.sex && this.isValidDate && this.isValidSex)
      ) {
        this.sendForm();
      } else {
        this.$nextTick(() => this.scrollToError());
      }
    },
    async sendForm() {
      this.loading = true;

      if (!(await this.checkUserVerification(false))) {
        this.loading = false;
        return false;
      }

      let updatedFields = {};

      if (this.date) {
        let birthDateUpdated = !this.$auth.user.birthDate;
        if (!birthDateUpdated) {
          const localDate = new Date(this.date);
          const webUserDate = new Date(this.$auth.user.birthDate);
          birthDateUpdated =
            localDate.getFullYear() !== webUserDate.getFullYear() ||
            localDate.getMonth() !== webUserDate.getMonth() ||
            localDate.getDate() !== webUserDate.getDate();
        }

        if (birthDateUpdated) {
          updatedFields.birthDate = this.date;
        }
      }

      if (!!this.sex && this.sex !== this.$auth.user.gender) {
        updatedFields.sex = this.sex;
      }

      this.$http
        // TODO: migrate to patchUser helper
        .patch('/v3/fe/web-users', updatedFields)
        .then(async () => {
          this.sendAnalyticsUl({
            activity: 'prevention_personal_details_saved',
            partner_id: this.partnerData?.id,
            partner_name: this.partnerData?.name
          });
          this.$gtm.push({ event: 'form_sent_success', 'form.name': 'prevence' });
          await this.$auth.fetchUser();
          await this.getPreventionData();
          await this.getAllDefaultFormData();
        })
        .catch(error => {
          this.handleError({ error: error, code: '0x00000024' });
        })
        .finally(() => {
          this.loading = false;
          this.dialog = false;
        });
    }
  }
};
</script>
